import React, { useReducer } from 'react';

import { Button, CircularProgress, Dialog } from '@material-ui/core';
import { IconButton, Paper, Typography } from '@material-ui/core';
import { MdClose } from 'react-icons/md';

import API from 'utils/api';
import { AutoComplete } from 'components';
import { useDialog, useSources } from 'hooks';
import { call } from 'utils';

import styles from './AddConditionDialog.module.scss';

const reducer = (state, action) => {
  switch (action.type) {
    case 'CLEAR': {
      const mode = action.payload || state.mode || 'tag';

      return {
        ...state,
        condition: {},
        values: null,
        mode,
        categories: mode === 'tag' ? state.tagCategories : state.topicCategories,
      };
    }

    case 'INIT':
      return {
        ...state,
        tagCategories: action.payload.tags || [],
        topicCategories: action.payload.topics || [],
      };

    case 'SET_CATEGORY':
      return {
        ...state,
        condition: {
          category: action.payload,
          operator: state.mode === 'tag' && !state.singleOp ? null : 'is',
        },
        values: null,
      };

    case 'SET_OPERATOR':
      return { ...state, condition: { ...state.condition, operator: action.payload } };

    case 'SET_VALUE':
      return { ...state, condition: { ...state.condition, value: action.payload } };

    case 'SET_VALUES':
      return { ...state, values: action.payload };

    default:
      return state;
  }
};

const AddConditionDialog = props => {
  const [state, dispatch] = useReducer(reducer, {
    condition: {},
    singleOp: props.singleOp,
  });

  const { categories, condition, values, mode } = state;
  const sources = useSources();

  const { isOpening } = useDialog(props.open, {
    onOpen: () => dispatch({ type: 'CLEAR', payload: props.mode || 'tag' }),

    onMount: async () => {
      const [tags, topics] = await Promise.all([
        API.getTagCategories(sources).then(({ data }) => data),
        API.getTopicCategories(sources).then(({ data }) => data),
      ]);

      dispatch({
        type: 'INIT',
        payload: {
          tags: tags ? tags.map(({ _id: id, name: text }) => ({ id, text })) : null,
          topics: topics ? topics.map(({ _id: id, name: text }) => ({ id, text })) : null,
        },
      });
    },
  });

  if (isOpening) {
    return null;
  }

  const renderInput = () => {
    if (!categories) {
      return <CircularProgress size={32} />;
    }

    if (!condition.category) {
      return (
        <AutoComplete
          key="category"
          items={categories}
          placeholder="Type a category"
          onChange={e => {
            if (e.target.value) {
              const category = categories.find(({ id }) => id === e.target.value);
              dispatch({ type: 'SET_CATEGORY', payload: category });

              const getter =
                mode === 'tag'
                  ? API.getTagsByCategory(category.text, sources)
                  : API.getTopicsByCategory({ categoryId: category.id }, sources);

              getter.then(({ data }) =>
                dispatch({
                  type: 'SET_VALUES',
                  payload: data.map(({ _id: id, name: text }) => ({ id, text })),
                })
              );
            }
          }}
        />
      );
    }

    if (!values) {
      return <CircularProgress size={32} />;
    }

    if (!values.length) {
      return (
        <Typography variant="body2" style={{ textAlign: 'center' }}>
          No values found
        </Typography>
      );
    }

    if (!condition.operator) {
      return (
        <AutoComplete
          key="operator"
          items={['is', 'is not'].map(it => ({ id: it, text: it }))}
          placeholder="Type an operator"
          onChange={e =>
            e.target.value
              ? dispatch({ type: 'SET_OPERATOR', payload: e.target.value })
              : null
          }
        />
      );
    }

    return (
      <AutoComplete
        key="value"
        items={values}
        placeholder="Type a value"
        value={(condition.value || {}).text || ''}
        onChange={e => {
          if (e.target.value) {
            const value = (values || []).find(({ id }) => id === e.target.value);
            dispatch({ type: 'SET_VALUE', payload: value });
          }
        }}
      />
    );
  };

  const { category, operator, value } = condition;

  return (
    <Dialog open={props.open} onClose={props.onClose}>
      <Paper className={styles.modal}>
        <div className={styles.top}>
          <IconButton onClick={props.onClose}>
            <MdClose fontSize="medium" />
          </IconButton>
        </div>

        <div className={styles.center}>
          <div className={styles.header}>
            <Typography variant="caption">{(category || {}).text || ''}</Typography>
            <Typography variant="caption">{operator || ''}</Typography>
            <Typography variant="caption">{(value || {}).text || ''}</Typography>
          </div>

          <div className={styles.body}>{renderInput()}</div>

          <div className={styles.footer}>
            <Button onClick={() => dispatch({ type: 'CLEAR' })}>Clear</Button>

            <Button
              variant="contained"
              color="primary"
              disabled={!category || !operator || !value}
              onClick={() => call(props.onAdd(condition))}
            >
              Add
            </Button>
          </div>
        </div>
      </Paper>
    </Dialog>
  );
};

export default AddConditionDialog;
